import { Wifi, WifiOff, Loader2 } from "lucide-react";
import { useNetworkStatus } from "@/hooks/use-network-status";
import { cn } from "@/libs/utils";

export function NetworkStatus() {
  const { isOnline, connectionType, effectiveType } = useNetworkStatus();

  if (typeof window === "undefined") return null;

  const getStatusIcon = () => {
    if (!isOnline) {
      return <WifiOff className="h-4 w-4 text-destructive" />;
    }

    // Show different icons based on connection quality
    if (effectiveType === "4g" || effectiveType === "slow-2g") {
      return <Wifi className="h-4 w-4 text-green-500" />;
    } else if (effectiveType === "3g") {
      return <Wifi className="h-4 w-4 text-yellow-500" />;
    } else if (effectiveType === "2g") {
      return <Wifi className="h-4 w-4 text-orange-500" />;
    }

    return <Wifi className="h-4 w-4 text-green-500" />;
  };

  const getStatusText = () => {
    if (!isOnline) {
      return "Offline";
    }

    if (connectionType === "wifi") {
      return "WiFi";
    } else if (connectionType === "cellular") {
      return effectiveType ? effectiveType.toUpperCase() : "Cellular";
    } else if (connectionType === "ethernet") {
      return "Ethernet";
    }

    return "Online";
  };

  return (
    <div
      className={cn(
        "flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium transition-colors",
        isOnline
          ? "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300"
          : "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300"
      )}
    >
      {getStatusIcon()}
      <span>{getStatusText()}</span>
    </div>
  );
}

// Compact version for mobile or minimal UI
export function NetworkStatusCompact() {
  const { isOnline } = useNetworkStatus();

  if (typeof window === "undefined") return null;

  return (
    <div
      className={cn(
        "flex items-center justify-center w-2 h-2 rounded-full transition-colors",
        isOnline ? "bg-green-500" : "bg-red-500"
      )}
      title={isOnline ? "Online" : "Offline"}
    />
  );
}

// Status bar component like Telegram
export function NetworkStatusBar() {
  const { isOnline, connectionType, effectiveType } = useNetworkStatus();

  if (typeof window === "undefined") return null;

  return (
    <div
      className={cn(
        "flex items-center gap-2 px-2 py-1 text-xs transition-colors border-t",
        isOnline
          ? "bg-background text-muted-foreground border-border"
          : "bg-destructive/10 text-destructive border-destructive/20"
      )}
    >
      {isOnline ? (
        <>
          <Wifi className="h-3 w-3" />
          <span>
            {connectionType === "wifi"
              ? "Connected via WiFi"
              : connectionType === "cellular"
              ? `Connected via ${
                  effectiveType ? effectiveType.toUpperCase() : "Cellular"
                }`
              : "Connected"}
          </span>
        </>
      ) : (
        <>
          <WifiOff className="h-3 w-3" />
          <span>No internet connection</span>
        </>
      )}
    </div>
  );
}
